import React, { useEffect, useState, useRef } from 'react';
import useWindowSize from './utils/useWindowSize'; // small hook below
import Register from './pages/Register';
import Login from './pages/Login';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ConsultantDashboard from './pages/ConsultantDashboard';
import api from './services/api';
import theme from './styles/theme';
import styles from './styles/App.module.css';

// Simple Navbar component with dashboard selector for Admin/Consultant
const Navbar = ({ user, onLogout, setPage }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width !== undefined && width < 800;

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [saving, setSaving] = useState(false);
 
   // fee dropdown state (kept)
   const [feeOpen, setFeeOpen] = useState(false);

  // refs for click-outside handling (wrap button + menu)
  const feeRef = useRef(null);
  const dashRef = useRef(null);
  const menuRef = useRef(null);

  // close dropdowns when clicking outside or pressing Escape
  useEffect(() => {
    const onDocClick = (e) => {
      if (feeOpen && feeRef.current && !feeRef.current.contains(e.target)) setFeeOpen(false);
      if (dashOpen && dashRef.current && !dashRef.current.contains(e.target)) setDashOpen(false);
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setFeeOpen(false);
        setDashOpen(false);
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [feeOpen, dashOpen, menuOpen]);

   const handleLogout = () => {
     setMenuOpen(false);
     onLogout && onLogout();
   };
 
   const changePassword = async (e) => {
     e && e.preventDefault();
     if (!currentPwd || !newPwd) return alert('Enter passwords');
     if (newPwd !== confirmPwd) return alert('New passwords do not match');
     setSaving(true);
     try {
       await api.post('/users/change-password', { currentPassword: currentPwd, newPassword: newPwd });
       alert('Password changed');
       setShowPwd(false);
       setMenuOpen(false);
       setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
     } catch (err) {
       alert(err?.response?.data?.message || 'Change password failed');
     } finally {
       setSaving(false);
     }
   };

   // choose dashboard target: 'auto' uses role-default, otherwise 'user'|'consultant'|'admin'
   const chooseDashboard = (target = 'auto') => {
     setDashOpen(false);
     setMobileOpen(false);
     if (target === 'auto') return setPage('dashboard');
     return setPage(`dashboard:${target}`);
   };
 
  // Desktop layout
  return (
    <>
      <nav className={styles.navbar} style={{ padding: isMobile ? '8px 12px' : '10px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => { setPage('home'); setMobileOpen(false); }} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} aria-label="Go to home">
            <img src={`${process.env.PUBLIC_URL || ''}/Logo.png`} alt="Doc Sheba" style={{ height: 40 }} />
          </button>
        </div>

        {/* mobile: show hamburger; desktop: show full controls */}
        {!isMobile ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* desktop: existing controls (unchanged) */}
            {/* Fees button */}
            <div style={{ position: 'relative' }} ref={feeRef}>
              <button onClick={() => setFeeOpen(s => !s)} className={styles.navBtn} aria-expanded={feeOpen} style={{ padding: '8px 12px', borderRadius: 8 }}>Fees ▾</button>
              {feeOpen && (
                <div role="dialog" aria-label="Fees" style={{ position: 'absolute', right: 0, marginTop: 8, width: 320, background: theme.colors.cardBg, borderRadius: 10, padding: 12, boxShadow: theme.shadows.pop, zIndex: 2000 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>Tax Return Submission Fees</div>
                    <button onClick={() => setFeeOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Close fees">✕</button>
                  </div>
                  <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.4 }}>
                    ️<strong>Our fees for tax return submission service:</strong>
                    <div style={{ marginBottom: 6 }}><strong>Zero taxpayer</strong> — ৳500</div>
                    <div style={{ marginBottom: 6 }}><strong>Minimum taxpayer</strong> — ৳1,000</div>
                    <div style={{ marginBottom: 6 }}><strong>Above minimum</strong> — ৳2,000–৳5,000</div>
                  </div>
                </div>
              )}
            </div>

            {!user && (
              <>
                <button onClick={() => setPage('login')} className={styles.navBtn}>Login</button>
                <button onClick={() => setPage('register')} className={styles.navBtn}>Register</button>
              </>
            )}

            {user && (
              <>
                <div style={{ position: 'relative' }} ref={dashRef}>
                  <button onClick={() => setDashOpen(d => !d)} className={styles.navBtn} style={{ padding: '8px 12px', borderRadius: 8 }}>Dashboard ▾</button>
                  {dashOpen && (
                    <div style={{ position: 'absolute', right: 0, marginTop: 8, background: theme.colors.cardBg, color: '#111', borderRadius: 6, boxShadow: theme.shadows.pop, minWidth: 200, overflow: 'hidden', zIndex: 2000 }}>
                      {user.role === 'Admin' && <>
                        <button onClick={() => chooseDashboard('user')} className={styles.menuItem}>User Dashboard</button>
                        <button onClick={() => chooseDashboard('consultant')} className={styles.menuItem}>Consultant Dashboard</button>
                        <div style={{ height: 1, background: '#efefef' }} />
                        <button onClick={() => chooseDashboard('admin')} className={styles.menuItem}>Admin Dashboard</button>
                      </>}
                      {user.role === 'Consultant' && <>
                        <button onClick={() => chooseDashboard('consultant')} className={styles.menuItem}>Consultant Dashboard</button>
                        <button onClick={() => chooseDashboard('user')} className={styles.menuItem}>User Dashboard</button>
                      </>}
                      {user.role !== 'Admin' && user.role !== 'Consultant' && (
                        <button onClick={() => chooseDashboard('user')} className={styles.menuItem}>My Dashboard</button>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ position: 'relative' }} ref={menuRef}>
                  <button onClick={() => setMenuOpen(s => !s)} className={styles.navBtn} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8 }} aria-haspopup="true" aria-expanded={menuOpen}>
                    {user.profile_picture ? <img src={user.profile_picture.startsWith('http') ? user.profile_picture : (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL + user.profile_picture : user.profile_picture)} alt={user.name || 'User'} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(0,0,0,0.08)' }} /> : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5z" stroke="#1976d2" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M20 21c0-3.866-3.582-7-8-7s-8 3.134-8 7" stroke="#1976d2" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    <span style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{user.name ? (user.name.split(' ')[0]) : 'Account'}</span>
                  </button>
                  {menuOpen && (
                    <div style={{ position: 'absolute', right: 0, marginTop: 8, background: theme.colors.cardBg, color: '#111', borderRadius: 6, boxShadow: theme.shadows.pop, minWidth: 200, overflow: 'hidden', zIndex: 2000 }}>
                      <button onClick={() => { setShowPwd(true); setMenuOpen(false); }} className={styles.menuItem}>Change password</button>
                      <div style={{ height: 1, background: '#efefef' }} />
                      <button onClick={handleLogout} style={{ display: 'block', width: '100%', padding: '10px 12px', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', color: '#d32f2f' }}>Logout</button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          // mobile: compact right area with hamburger
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setMobileOpen(s => !s)} aria-expanded={mobileOpen} aria-label="Open menu" className={styles.hamburger}>
              ☰
            </button>
          </div>
        )}
      </nav>

      {/* Mobile full-screen menu */}
      {isMobile && mobileOpen && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(0,0,0,0.4)' }} onClick={() => setMobileOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: 420, height: '100%', background: '#fff', padding: 16, boxShadow: '0 6px 24px rgba(0,0,0,0.2)', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <img src={`${process.env.PUBLIC_URL || ''}/Logo.png`} alt="Doc Sheba" style={{ height: 36 }} />
              <button onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>✕</button>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <button onClick={() => { setPage('home'); setMobileOpen(false); }} className={styles.mobileMenuItem}>Home</button>
              <button onClick={() => { setFeeOpen(s => !s); }} className={styles.mobileMenuItem}>Fees</button>
              {!user && <>
                <button onClick={() => { setPage('login'); setMobileOpen(false); }} className={styles.mobileMenuItem}>Login</button>
                <button onClick={() => { setPage('register'); setMobileOpen(false); }} className={styles.mobileMenuItem}>Register</button>
              </>}
              {user && <>
                <div style={{ borderTop: '1px solid #eee', paddingTop: 8 }} />
                <div style={{ fontWeight: 700 }}>{user.name || 'Account'}</div>
                <button onClick={() => chooseDashboard('user')} className={styles.mobileMenuItem}>My Dashboard</button>
                {user.role === 'Consultant' && <button onClick={() => chooseDashboard('consultant')} className={styles.mobileMenuItem}>Consultant Dashboard</button>}
                {user.role === 'Admin' && <>
                  <button onClick={() => chooseDashboard('user')} className={styles.mobileMenuItem}>User Dashboard</button>
                  <button onClick={() => chooseDashboard('consultant')} className={styles.mobileMenuItem}>Consultant Dashboard</button>
                  <button onClick={() => chooseDashboard('admin')} className={styles.mobileMenuItem}>Admin Dashboard</button>
                </>}
                <button onClick={() => { setShowPwd(true); setMobileOpen(false); }} className={styles.mobileMenuItem}>Change password</button>
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className={styles.mobileMenuItem} style={{ color: '#d32f2f' }}>Logout</button>
              </>}
            </div>
          </div>
        </div>
      )}

      {/* Change password modal */}
      {showPwd && (
        <div style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <form onSubmit={changePassword} style={{ background: '#fff', padding: 20, borderRadius: 8, width: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Change password</h3>
              <button type="button" onClick={() => setShowPwd(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>✕</button>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <label>Current password</label>
              <input type="password" value={currentPwd} required onChange={e => setCurrentPwd(e.target.value)} />
              <label>New password</label>
              <input type="password" value={newPwd} required onChange={e => setNewPwd(e.target.value)} />
              <label>Confirm new password</label>
              <input type="password" value={confirmPwd} required onChange={e => setConfirmPwd(e.target.value)} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" onClick={() => setShowPwd(false)} style={{ padding: '8px 12px' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '8px 12px', background: '#0b69ff', color: '#fff', border: 'none' }}>{saving ? 'Saving...' : 'Change'}</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
 };

const Footer = () => (
  <footer className={styles.footer}>
    &copy; {new Date().getFullYear()} Doc Sheba. All rights reserved.
  </footer>
);

// Modern Homepage (carousel + concise hero + quick services)
const Home = () => {
  // carousel images from public/image folder (place hero1..hero4 in public/image/)
  const images = [
    `${process.env.PUBLIC_URL || ''}/image/hero1.jpg`,
    `${process.env.PUBLIC_URL || ''}/image/hero2.jpg`,
    `${process.env.PUBLIC_URL || ''}/image/hero3.jpg`,
    `${process.env.PUBLIC_URL || ''}/image/hero4.jpg`
  ];

  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % images.length), 3500);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ maxWidth: 1100, margin: '2.5rem auto', padding: '1rem' }}>
      {/* Carousel */}
      <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: theme.shadows.pop }}>
        <div style={{ position: 'relative', height: 340, background: '#f3f6fb' }}>
          <img
            src={images[idx]}
            alt="hero"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'opacity 600ms ease-in-out' }}
          />
          <div style={{ position: 'absolute', left: 16, bottom: 16, background: 'rgba(0,0,0,0.5)', color: '#fff', padding: '10px 14px', borderRadius: 10 }}>
            <div style={{ fontWeight: 700 }}>Doc Sheba</div>
            <div style={{ fontSize: 13 }}>Secure documents • Tax & VAT • Accounting</div>
          </div>
          <div style={{ position: 'absolute', right: 12, bottom: 12, display: 'flex', gap: 6 }}>
            {images.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} aria-label={`show ${i+1}`} style={{
                width: 10, height: 10, borderRadius: 10, border: 'none',
                background: i === idx ? '#0b69ff' : 'rgba(255,255,255,0.7)', cursor: 'pointer'
              }} />
            ))}
          </div>
        </div>
      </div>

      {/* Hero + description */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 20, marginTop: 22 }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: '2rem', color: '#0f1f3a' }}>All your important documents & tax services — in one secure place</h1>
          <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.6, maxWidth: 1300 }}>
            Doc Sheba securely stores your documents and connects you with experienced tax and accounting professionals for consultancy, tax return submission, VAT services, company affairs and outsourced bookkeeping. Fast, confidential and compliant.
          </p>
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <button onClick={() => window.location.hash = '#register'} style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: '#0b69ff', color: '#fff', boxShadow: theme.shadows.pop }}>Get Started</button>
            <button onClick={() => window.location.hash = '#contact'} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #e6eef8', background: '#fff', color: '#0b69ff' }}>Contact</button>
          </div>
        </div>

        {/* Quick Services (moved below description) */}
        <div style={{ marginTop: 8 }}>
          <h3 style={{ margin: '12px 0 8px', color: '#0f1f3a' }}>Quick Services</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: 14, boxShadow: theme.shadows.soft }}>
              <strong>Tax Consultancy</strong>
              <div style={{ color: '#556', marginTop: 8, fontSize: 13 }}>Expert tax advice tailored to your needs.</div>
            </div>
            <div style={{ background: '#fff', borderRadius: 10, padding: 14, boxShadow: theme.shadows.soft }}>
              <strong>Tax Return Submission</strong>
              <div style={{ color: '#556', marginTop: 8, fontSize: 13 }}>Timely filing for zero/minimum/complex cases.</div>
            </div>
            <div style={{ background: '#fff', borderRadius: 10, padding: 14, boxShadow: theme.shadows.soft }}>
              <strong>VAT Services</strong>
              <div style={{ color: '#556', marginTop: 8, fontSize: 13 }}>Registration, compliance and return filing.</div>
            </div>
            <div style={{ background: '#fff', borderRadius: 10, padding: 14, boxShadow: theme.shadows.soft }}>
              <strong>Accounting & Company Affairs</strong>
              <div style={{ color: '#556', marginTop: 8, fontSize: 13 }}>Bookkeeping, statutory compliance and outsourcing.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App(/* props */) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const restore = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        const res = await api.get('/users/profile');
        setUser(res.data);
      } catch (err) {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      }
    };
    restore();
  }, []);

  // expose setUser/onLogout handlers to children as needed
  const handleLogout = async () => {
    try {
      await api.post('/auth/logout'); // if you have logout endpoint
    } catch (e) { /* ignore */ }
    // remove persisted token and Authorization header
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const [page, setPage] = useState('home');

  const handleLogin = (userData) => {
    setUser(userData);
    setPage('dashboard');
  };

  const handleRegistered = () => setPage('login');

  let content;
  if (!user) {
    if (page === 'login') content = <Login onLogin={handleLogin} onNavigate={setPage} />;
    else if (page === 'register') content = <Register onRegistered={handleRegistered} onNavigate={setPage} />;
    else content = <Home />;
  } else {
    // dashboard routing: supports explicit targets via 'dashboard', 'dashboard:user', 'dashboard:consultant', 'dashboard:admin'
    if (String(page).startsWith('dashboard')) {
      const parts = String(page).split(':');
      const target = parts[1] || 'auto';
      if (target === 'user') content = <UserDashboard />;
      else if (target === 'consultant') content = <ConsultantDashboard />;
      else if (target === 'admin') content = <AdminDashboard />;
      else {
        // auto: fall back to role-based default
        if (user.role === 'Admin') content = <AdminDashboard />;
        else if (user.role === 'Consultant') content = <ConsultantDashboard />;
        else content = <UserDashboard />;
      }
    } else {
      content = <Home />;
    }
  }

  return (
    <div className={styles.appWrapper}>
      <Navbar user={user} onLogout={handleLogout} setPage={setPage} />
      {/*
        For dashboard pages we render content full-bleed (no .pageCard wrapper)
        to avoid extra white gap under navbar/sidebar. Non-dashboard pages keep
        the centered card layout.
      */}
      {String(page).startsWith('dashboard') ? (
        <main style={{ padding: 0 }}>
          {content}
        </main>
      ) : (
        <main style={{ padding: '1rem 0' }}>
          <div className={styles.container}>
            <div className={styles.pageCard}>
              {content}
            </div>
          </div>
        </main>
      )}
    </div>
  );
 }
 
 export default App;